import { useEffect, useRef, useState } from "react";
import { useInView } from "../hooks/useInView";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const GLYPHS = "!<>-_\\/[]{}-=+*^?#";

type TextScrambleProps = {
  /** Docelowy tekst - dokładnie ta sama treść co obecnie na stronie */
  text: string;
  /** Czas pełnego dekodowania w ms */
  durationMs?: number;
  className?: string;
};

/**
 * Efekt "dekodowania" tekstu w stylu terminala: znaki losowe stopniowo
 * układają się w docelowy napis, od lewej do prawej. Odpala się raz,
 * przy wejściu elementu w viewport.
 *
 * Idealny dla istniejących mono-labelek sekcji: "NASZA HISTORIA",
 * "ZAUFALI NAM", "OTWARTE ROLE · PRACA ZDALNA", "TWÓJ NASTĘPNY RUCH" -
 * treść zostaje identyczna, zmienia się tylko sposób pojawienia.
 *
 * Przy prefers-reduced-motion: tekst renderuje się od razu w całości.
 * Szerokość elementu jest stała (docelowy tekst renderowany niewidocznie
 * pod spodem), więc layout nie skacze podczas animacji.
 */
export function TextScramble({ text, durationMs = 900, className }: TextScrambleProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, { once: true, margin: "0px 0px -8% 0px" });
  const reducedMotion = usePrefersReducedMotion();
  const [display, setDisplay] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (!inView || startedRef.current) return;
    startedRef.current = true;

    const duration = reducedMotion ? Math.min(durationMs, 300) : durationMs;
    const start = performance.now();
    let raf: number;

    function tick(now: number) {
      const progress = Math.min((now - start) / duration, 1);
      const settled = Math.floor(progress * text.length);

      let out = "";
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === " ") {
          out += " ";
        } else if (i < settled) {
          out += ch;
        } else {
          out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        }
      }
      setDisplay(out);

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(text);
      }
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, text, durationMs, reducedMotion]); // reducedMotion affects duration only

  return (
    <span ref={ref} className={className} style={{ position: "relative", display: "inline-block" }}>
      {/* Niewidoczny docelowy tekst rezerwuje szerokość - zero skakania layoutu */}
      <span style={{ visibility: "hidden" }} aria-hidden="true">
        {text}
      </span>
      <span style={{ position: "absolute", inset: 0 }} aria-label={text}>
        {display}
      </span>
    </span>
  );
}
